import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";
import FavoritesPage, { type FavoriteItem } from "@/components/pages/FavoritesPage";

type FavoriteLink = {
  id: string;
  slug: string;
  title: string;
  description: string;
  brand: string | null;
  categoryId: string;
  mediaId: string | null;
  externalImageUrl: string | null;
  destinationType: "external" | "shopProduct";
  externalUrl: string | null;
  shopProductId: string | null;
  discountCode: string | null;
  tag: string | null;
  keywords: string | null;
  featured: boolean;
  sortOrder: number;
  status: "draft" | "published";
  categoryName?: string;
  categorySlug?: string;
  imageUrl?: string | null;
  href?: string;
  openInNewTab?: boolean;
  ctaLabel?: string;
};
type Category = { id: string; name: string; slug: string };
type ViewMode = "rows" | "grid";
type FavoriteSortKey = "title" | "category" | "destination" | "status" | "sort";

type FormState = {
  title: string;
  description: string;
  slug: string;
  brand: string;
  categoryId: string;
  mediaId: string;
  externalImageUrl: string;
  destinationType: "external" | "shopProduct";
  externalUrl: string;
  shopProductId: string;
  discountCode: string;
  tag: string;
  keywords: string;
  featured: boolean;
  sortOrder: string;
  status: "draft" | "published";
};

const empty: FormState = {
  title: "",
  description: "",
  slug: "",
  brand: "",
  categoryId: "",
  mediaId: "",
  externalImageUrl: "",
  destinationType: "external",
  externalUrl: "",
  shopProductId: "",
  discountCode: "",
  tag: "",
  keywords: "",
  featured: false,
  sortOrder: "0",
  status: "draft",
};

function toForm(l: FavoriteLink): FormState {
  return {
    title: l.title,
    description: l.description,
    slug: l.slug,
    brand: l.brand ?? "",
    categoryId: l.categoryId,
    mediaId: l.mediaId ?? "",
    externalImageUrl: l.externalImageUrl ?? "",
    destinationType: l.destinationType,
    externalUrl: l.externalUrl ?? "",
    shopProductId: l.shopProductId ?? "",
    discountCode: l.discountCode ?? "",
    tag: l.tag ?? "",
    keywords: l.keywords ?? "",
    featured: l.featured,
    sortOrder: l.sortOrder.toString(),
    status: l.status,
  };
}

function moveLink(items: FavoriteLink[], sourceIndex: number, targetIndex: number): FavoriteLink[] {
  if (
    sourceIndex === targetIndex ||
    sourceIndex < 0 ||
    targetIndex < 0 ||
    sourceIndex >= items.length ||
    targetIndex >= items.length
  ) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
}

function withSequentialSortOrder(items: FavoriteLink[]): FavoriteLink[] {
  return items.map((link, index) => ({ ...link, sortOrder: index + 1 }));
}

function sameOrder(a: FavoriteLink[], b: FavoriteLink[]): boolean {
  return a.length === b.length && a.every((link, index) => link.id === b[index]?.id);
}

function favoriteSearchText(link: FavoriteLink, categoryName: string) {
  return [
    link.title,
    link.slug,
    link.description,
    link.brand,
    categoryName,
    link.destinationType,
    link.externalUrl,
    link.discountCode,
    link.tag,
    link.keywords,
    link.featured ? "featured" : "",
    link.status,
    formatInteger(link.sortOrder),
  ]
    .filter(Boolean)
    .join(" ");
}

const favoriteSorters: Record<FavoriteSortKey, (row: FavoriteLink) => string | number> = {
  title: (row) => row.title,
  category: (row) => row.categoryName ?? row.categoryId,
  destination: (row) => row.destinationType,
  status: (row) => row.status,
  sort: (row) => row.sortOrder,
};

export default function AdminFavoriteLinks() {
  const [list, setList] = useState<FavoriteLink[]>([]);
  const [committedList, setCommittedList] = useState<FavoriteLink[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [editing, setEditing] = useState<FavoriteLink | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [view, setView] = useState<ViewMode>("rows");
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderMessage, setOrderMessage] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ favoriteLinks: FavoriteLink[] }>("/api/admin/favorite-links");
    setList(r.favoriteLinks);
    setCommittedList(r.favoriteLinks);
    setOrderDirty(false);
    setDragId(null);
    setDragIndex(null);
    setDropIndex(null);
  }

  useEffect(() => {
    Promise.all([
      refresh(),
      api.get<{ categories: Category[] }>("/api/admin/categories").then((r) => setCats(r.categories)),
    ]).finally(() => setLoaded(true));
  }, []);

  const categoryById = useMemo(() => new Map(cats.map((cat) => [cat.id, cat])), [cats]);
  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All categories" },
      ...cats.map((cat) => ({ value: cat.id, label: cat.name })),
    ],
    [cats],
  );
  const filters = useMemo(
    () => [
      (row: FavoriteLink) => statusFilter === "all" || row.status === statusFilter,
      (row: FavoriteLink) => categoryFilter === "all" || row.categoryId === categoryFilter,
      (row: FavoriteLink) =>
        featuredFilter === "all" ||
        (featuredFilter === "featured" && row.featured) ||
        (featuredFilter === "standard" && !row.featured),
    ],
    [categoryFilter, featuredFilter, statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<FavoriteLink, FavoriteSortKey>({
    rows: list,
    search,
    initialSort: { key: "sort", direction: "asc" },
    sorters: favoriteSorters,
    searchText: (row) => favoriteSearchText(row, categoryById.get(row.categoryId)?.name ?? ""),
    filters,
  });

  const favoriteItems = useMemo<FavoriteItem[]>(
    () =>
      list.map((link) => {
        const cat = categoryById.get(link.categoryId);
        const href =
          link.href ??
          (link.destinationType === "shopProduct" && link.shopProductId
            ? `/shop/${link.shopProductId}`
            : (link.externalUrl ?? "#"));

        return {
          id: link.id,
          title: link.title,
          description: link.description,
          brand: link.brand,
          categoryName: link.categoryName || cat?.name || "Uncategorized",
          categorySlug: link.categorySlug || cat?.slug || link.categoryId,
          tag: link.tag,
          keywords: link.keywords,
          discountCode: link.discountCode,
          featured: link.featured,
          status: link.status,
          sortOrder: link.sortOrder,
          imageUrl: link.imageUrl ?? link.externalImageUrl,
          href,
          openInNewTab: link.openInNewTab ?? link.destinationType === "external",
          ctaLabel:
            link.ctaLabel ??
            (link.destinationType === "shopProduct" ? "View in shop" : "Visit link"),
        };
      }),
    [categoryById, list],
  );

  function startNew() {
    setForm({ ...empty, categoryId: cats[0]?.id ?? "" });
    setEditing("new");
    setErr(null);
  }
  function startEdit(l: FavoriteLink) {
    setForm(toForm(l));
    setEditing(l);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      title: form.title,
      description: form.description,
      slug: form.slug || undefined,
      brand: form.brand || null,
      categoryId: form.categoryId,
      mediaId: form.mediaId || null,
      externalImageUrl: form.externalImageUrl || null,
      destinationType: form.destinationType,
      externalUrl: form.externalUrl || null,
      shopProductId: form.shopProductId || null,
      discountCode: form.discountCode || null,
      tag: form.tag || null,
      keywords: form.keywords || null,
      featured: form.featured,
      sortOrder: parseInt(form.sortOrder || "0", 10),
      status: form.status,
    };
    try {
      if (editing === "new") await api.post("/api/admin/favorite-links", payload);
      else if (editing) await api.put(`/api/admin/favorite-links/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(l: FavoriteLink) {
    if (!confirm(`Delete "${l.title}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/favorite-links/${l.id}`);
    await refresh();
  }

  async function confirmOrder() {
    setSavingOrder(true);
    setOrderMessage("Saving order...");
    setOrderError(null);
    try {
      const nextList = withSequentialSortOrder(list);
      await api.put("/api/admin/favorite-links/order", {
        ids: nextList.map((link) => link.id),
      });
      setList(nextList);
      setCommittedList(nextList);
      setOrderDirty(false);
      setOrderMessage("Order saved.");
    } catch (e2) {
      setOrderMessage("Order changes are still pending.");
      setOrderError(e2 instanceof Error ? e2.message : "Order save failed");
    } finally {
      setSavingOrder(false);
    }
  }

  function cancelOrder() {
    setList(committedList);
    setOrderDirty(false);
    setDragId(null);
    setDragIndex(null);
    setDropIndex(null);
    setOrderMessage("Order changes canceled.");
    setOrderError(null);
  }

  function handleDragStart(sourceIndex: number) {
    if (savingOrder) return;
    const link = list[sourceIndex];
    if (!link) return;
    setDragId(link.id);
    setDragIndex(sourceIndex);
    setDropIndex(sourceIndex);
    setOrderMessage(null);
    setOrderError(null);
  }

  function handleDragOver(targetIndex: number) {
    if (savingOrder || dragId === null) return;
    setList((currentList) => {
      const sourceIndex = currentList.findIndex((link) => link.id === dragId);
      if (sourceIndex === -1 || sourceIndex === targetIndex) return currentList;

      const moved = withSequentialSortOrder(moveLink(currentList, sourceIndex, targetIndex));
      setOrderDirty(!sameOrder(moved, committedList));
      return moved;
    });
    setDragIndex(targetIndex);
    setDropIndex(targetIndex);
    setOrderMessage("Order changes not saved yet.");
    setOrderError(null);
  }

  function handleDrop(targetIndex: number) {
    if (savingOrder || dragId === null) return;
    setList((currentList) => {
      const sourceIndex = currentList.findIndex((link) => link.id === dragId);
      if (sourceIndex === -1 || sourceIndex === targetIndex) return currentList;

      const moved = withSequentialSortOrder(moveLink(currentList, sourceIndex, targetIndex));
      setOrderDirty(!sameOrder(moved, committedList));
      return moved;
    });
    setDragId(null);
    setDragIndex(null);
    setDropIndex(null);
    if (orderDirty) setOrderMessage("Review order changes, then confirm or cancel.");
  }

  function handleDragEnd() {
    setDragId(null);
    setDragIndex(null);
    setDropIndex(null);
  }

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Favorite Links</h1>
        {!editing && (
          <div className="hdr-actions">
            <div className="view-toggle" aria-label="Favorite links view">
              <button
                type="button"
                className={`view-option ${view === "rows" ? "active" : ""}`}
                onClick={() => setView("rows")}
              >
                Rows
              </button>
              <button
                type="button"
                className={`view-option ${view === "grid" ? "active" : ""}`}
                onClick={() => setView("grid")}
              >
                Grid
              </button>
            </div>
            <button type="button" className="btn-woven" onClick={startNew}>
              + New favorite link
            </button>
          </div>
        )}
      </div>

      {!editing && orderDirty ? (
        <div className="order-review">
          <div>
            <strong>Order changes pending</strong>
            <span>Confirm this card order or cancel to restore the last saved order.</span>
          </div>
          <div className="order-review-actions">
            <button type="button" className="btn-woven" onClick={confirmOrder} disabled={savingOrder}>
              {savingOrder ? "Saving..." : "Confirm order"}
            </button>
            <button type="button" className="btn-ghost" onClick={cancelOrder} disabled={savingOrder}>
              Cancel changes
            </button>
          </div>
        </div>
      ) : null}

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New favorite link" : editing.title}
            eyebrow={editing === "new" ? "Create favorite link" : "Edit favorite link"}
            backLabel="Back to favorite links"
            onBack={() => setEditing(null)}
          />
          <div className="grid2">
            <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            <Field label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          </div>
          <Field
            label="Description"
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            textarea
            required
          />
          <div className="grid2">
            <label className="fld">
              <span>Category</span>
              <select
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
          </div>
          <div className="grid2">
            <label className="fld">
              <span>Destination type</span>
              <select
                value={form.destinationType}
                onChange={(e) =>
                  setForm({ ...form, destinationType: e.target.value as "external" | "shopProduct" })
                }
              >
                <option value="external">External URL</option>
                <option value="shopProduct">Shop product</option>
              </select>
            </label>
            {form.destinationType === "external" ? (
              <Field
                label="External URL"
                value={form.externalUrl}
                onChange={(v) => setForm({ ...form, externalUrl: v })}
              />
            ) : (
              <Field
                label="Shop product ID"
                value={form.shopProductId}
                onChange={(v) => setForm({ ...form, shopProductId: v })}
              />
            )}
          </div>
          <div className="grid2">
            <Field
              label="Media ID (optional)"
              value={form.mediaId}
              onChange={(v) => setForm({ ...form, mediaId: v })}
            />
            <Field
              label="External image URL"
              value={form.externalImageUrl}
              onChange={(v) => setForm({ ...form, externalImageUrl: v })}
            />
          </div>
          <div className="grid2">
            <Field
              label="Discount code"
              value={form.discountCode}
              onChange={(v) => setForm({ ...form, discountCode: v })}
            />
            <Field label="Tag" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} />
          </div>
          <Field
            label="Keywords"
            value={form.keywords}
            onChange={(v) => setForm({ ...form, keywords: v })}
          />
          <div className="grid2">
            <label className="fld">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as "draft" | "published" })}
              >
                <option value="draft">draft</option>
                <option value="published">published</option>
              </select>
            </label>
            <Field
              label="Sort order"
              value={form.sortOrder}
              onChange={(v) => setForm({ ...form, sortOrder: v })}
            />
          </div>
          <label className="fld checkbox">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            />
            <span style={{ marginLeft: 8 }}>Featured</span>
          </label>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
          <p className="note">Media is referenced by ID. Use the Media section to upload images.</p>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : list.length === 0 ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No favorite links yet.</p>
      ) : view === "grid" ? (
        <div className="favorites-admin-grid">
          <FavoritesPage
            items={favoriteItems}
            showHero={false}
            showSiteChrome={false}
            adminControls={{
              enabled: true,
              message: savingOrder ? "Saving order..." : (orderMessage ?? undefined),
              error: orderError ?? undefined,
              dragIndex,
              dropIndex,
              onDragStart: handleDragStart,
              onDragOver: handleDragOver,
              onDrop: handleDrop,
              onDragEnd: handleDragEnd,
              toolbar: (
                <div className="grid-toolbar">
                  <div>
                    <strong>Grid reorder</strong>
                    <span>Cards move while you drag. Confirm or cancel after drop.</span>
                  </div>
                  <span>{list.length} links</span>
                </div>
              ),
              renderCardMeta: (item, sourceIndex) => {
                const link = list[sourceIndex] ?? list.find((candidate) => candidate.id === String(item.id));
                if (!link) return null;

                return (
                  <div className="card-meta">
                    <div className="meta-row">
                      <span>Order {link.sortOrder}</span>
                      <span className={`status-pill ${link.status}`}>{link.status}</span>
                    </div>
                    <div className="card-actions">
                      <button
                        type="button"
                        className="btn-ghost sm"
                        draggable={false}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => startEdit(link)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-danger sm"
                        draggable={false}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => remove(link)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              },
            }}
          />
        </div>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search favorite links, brands, tags..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "draft", label: "Draft" },
                { value: "published", label: "Published" },
              ]}
            />
            <AdminTableFilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categoryOptions}
            />
            <AdminTableFilterSelect
              label="Featured"
              value={featuredFilter}
              onChange={setFeaturedFilter}
              options={[
                { value: "all", label: "All links" },
                { value: "featured", label: "Featured" },
                { value: "standard", label: "Not featured" },
              ]}
            />
          </AdminTableControls>
          {tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No favorite links match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Title" sortKey="title" sort={sort} onSort={requestSort} />
                  <SortableTh label="Category" sortKey="category" sort={sort} onSort={requestSort} />
                  <SortableTh label="Destination" sortKey="destination" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <SortableTh label="Sort" sortKey="sort" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <strong>{l.title}</strong>
                      <span className="slug">{l.slug}</span>
                    </td>
                    <td>{categoryById.get(l.categoryId)?.name ?? l.categoryId}</td>
                    <td>{l.destinationType}</td>
                    <td>{l.status}</td>
                    <td>{formatInteger(l.sortOrder)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(l)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(l)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .hdr-actions { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; justify-content: flex-end; }
  .view-toggle {
    display: inline-flex; align-items: center; padding: 3px; border-radius: 999px;
    background: #fff; border: 1px solid rgba(201,147,76,0.38); box-shadow: 0 8px 18px -14px rgba(8,45,61,0.35);
  }
  .view-option {
    border: none; background: transparent; border-radius: 999px; padding: 7px 13px;
    color: rgba(8,45,61,0.62); font-family: ${fonts.body}; font-size: 0.66rem;
    font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer;
  }
  .view-option.active { background: ${colors.navy}; color: #fff; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-ghost:disabled { opacity: 0.6; cursor: default; }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .order-review {
    display: flex; align-items: center; justify-content: space-between; gap: 1rem;
    margin: -8px 0 22px; padding: 0.85rem 1rem; border-radius: 14px;
    background: #fff; border: 1.5px dashed rgba(201,147,76,0.46);
    box-shadow: 0 12px 28px -22px rgba(8,45,61,0.38);
  }
  .order-review strong {
    display: block; font-family: ${fonts.body}; font-size: 0.76rem; font-weight: 800;
    letter-spacing: 0.08em; text-transform: uppercase; color: ${colors.navy};
  }
  .order-review span {
    display: block; margin-top: 0.15rem; font-family: ${fonts.body}; font-size: 0.82rem;
    font-weight: 600; color: rgba(8,45,61,0.62);
  }
  .order-review-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; justify-content: flex-end; }

  .favorites-admin-grid {
    border: 1.5px dashed rgba(201,147,76,0.34); border-radius: 16px; overflow: hidden;
    background: ${colors.cream};
  }
  .favorites-admin-grid section { padding-left: 1.25rem; padding-right: 1.25rem; }
  .grid-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
  .grid-toolbar strong {
    display: block; font-family: ${fonts.body}; font-size: 0.74rem; font-weight: 800;
    letter-spacing: 0.08em; text-transform: uppercase; color: ${colors.navy};
  }
  .grid-toolbar span {
    display: block; font-family: ${fonts.body}; font-size: 0.78rem; font-weight: 600;
    color: rgba(8,45,61,0.58);
  }
  .card-meta {
    border-top: 1px dashed rgba(201,147,76,0.28); border-bottom: 1px dashed rgba(201,147,76,0.28);
    padding: 0.65rem 0; display: grid; gap: 0.55rem;
  }
  .meta-row, .card-actions { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
  .meta-row {
    font-family: ${fonts.body}; font-size: 0.72rem; font-weight: 800;
    letter-spacing: 0.06em; text-transform: uppercase; color: rgba(8,45,61,0.58);
  }
  .status-pill {
    border-radius: 999px; padding: 4px 8px; background: rgba(96,107,76,0.12);
    color: #606b4c;
  }
  .status-pill.draft { background: rgba(212,96,106,0.13); color: ${colors.berryRose}; }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld textarea {
    width: 100%; min-height: 90px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 500; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld.checkbox { display: flex; align-items: center; padding-top: 0.5rem; }
  .fld.checkbox input { accent-color: ${colors.berryRose}; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
  .note { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 0.75rem; }
  @media (max-width: 720px) {
    .hdr { align-items: flex-start; flex-direction: column; }
    .hdr-actions { justify-content: flex-start; }
    .order-review { align-items: flex-start; flex-direction: column; }
    .order-review-actions { justify-content: flex-start; }
    .grid2 { grid-template-columns: 1fr; }
  }
`;
